import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, STATIC_URL, toast } from '../App';
import { useNavigate } from 'react-router-dom';
import { Rocket, Loader2, Edit, RefreshCw, Eye, Check, X, FileText, Link, Paperclip, Upload, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import VoiceRecorder from '../components/VoiceRecorder';

const AutoEditoExpress = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [additionalUrls, setAdditionalUrls] = useState('');
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadedAudio, setUploadedAudio] = useState(null);
  const [generatedArticles, setGeneratedArticles] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [regeneratingImage, setRegeneratingImage] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    content: '',
    summary: '',
    seo_title: '',
    seo_description: '',
    keywords: []
  });

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      const response = await axios.get(`${API}/wordpress-sites`);
      setSites(response.data);
    } catch (error) {
      toast.error('Erreur de chargement des sites');
    }
  };

  const handleRecordingComplete = async (audioFile) => {
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', audioFile);

      const response = await axios.post(`${API}/voice-memos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Mémo vocal transcrit ! 🎙️');
      setUploadedAudio(response.data);
    } catch (error) {
      toast.error('Erreur lors de la transcription');
    } finally {
      setUploading(false);
    }
  };

  const toggleSite = (siteId) => {
    setSelectedSites(prev =>
      prev.includes(siteId)
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);
    const uploadedResults = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        uploadedResults.push({
          id: response.data.id,
          name: file.name,
          path: response.data.file_path
        });
      } catch (error) {
        toast.error(`Erreur upload: ${file.name}`);
      }
    }

    setAdditionalFiles(prev => [...prev, ...uploadedResults]);
    setUploadingFiles(false);
    
    if (uploadedResults.length > 0) {
      toast.success(`${uploadedResults.length} fichier(s) ajouté(s)`);
    }
  };

  const removeFile = (fileId) => {
    setAdditionalFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleGenerate = async () => {
    if (!uploadedAudio && !additionalNotes && additionalFiles.length === 0 && !additionalUrls) {
      toast.error('Ajoutez du contenu (audio, notes, URLs ou fichiers)');
      return;
    }

    if (selectedSites.length === 0) {
      toast.error('Sélectionnez au moins un site de publication');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/articles/generate-multi`, {
        voice_memo_id: uploadedAudio?.id,
        transcription_override: uploadedAudio?.transcription, // Transcription modifiée par l'utilisateur
        additional_notes: additionalNotes,
        additional_urls: additionalUrls,
        additional_file_ids: additionalFiles.map(f => f.id),
        site_ids: selectedSites
      });

      toast.success(`${selectedSites.length} article(s) avec images générés ! 🚀`);
      setGeneratedArticles(response.data.articles);
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleEditArticle = async (siteId, articleData) => {
    try {
      const response = await axios.get(`${API}/articles/${articleData.article_id}`);
      const article = response.data;
      
      setEditForm({
        title: article.title,
        content: article.content,
        summary: article.summary || '',
        seo_title: article.seo_title || '',
        seo_description: article.seo_description || '',
        keywords: article.keywords || []
      });
      
      setEditingArticle({ siteId, articleId: articleData.article_id });
      setShowEditDialog(true);
    } catch (error) {
      toast.error('Erreur de chargement');
    }
  };

  const handleSaveEdit = async () => {
    try {
      const updatedArticle = {
        ...editForm,
        id: editingArticle.articleId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      await axios.put(`${API}/articles/${editingArticle.articleId}`, updatedArticle);
      
      // Update local state
      const updatedArticles = { ...generatedArticles };
      updatedArticles[editingArticle.siteId] = {
        ...updatedArticles[editingArticle.siteId],
        title: editForm.title,
        summary: editForm.summary,
        seo_title: editForm.seo_title,
        seo_description: editForm.seo_description
      };
      setGeneratedArticles(updatedArticles);
      
      toast.success('Article mis à jour !');
      setShowEditDialog(false);
      setEditingArticle(null);
    } catch (error) {
      toast.error('Erreur de sauvegarde');
    }
  };

  const handleRegenerateImage = async (siteId, articleId) => {
    setRegeneratingImage(articleId);
    try {
      const response = await axios.post(`${API}/articles/${articleId}/regenerate-image`);
      
      // Update local state
      const updatedArticles = { ...generatedArticles };
      updatedArticles[siteId].featured_image_url = response.data.image_url;
      setGeneratedArticles(updatedArticles);
      
      toast.success('Image régénérée ! 🎨');
    } catch (error) {
      toast.error('Erreur de génération d\'image');
    } finally {
      setRegeneratingImage(null);
    }
  };

  const handlePublishAll = async (publishStatus = 'publish') => {
    if (!generatedArticles) return;

    const statusLabel = publishStatus === 'publish' ? 'Publication' : 'Enregistrement en brouillon';
    toast.info(`${statusLabel} en cours...`);
    
    const results = [];
    for (const [siteId, articleData] of Object.entries(generatedArticles)) {
      try {
        await axios.post(`${API}/wordpress-sites/${siteId}/publish`, {
          article_id: articleData.article_id,
          categories: [],
          tags: [],
          publish_status: publishStatus
        });
        results.push({ site: articleData.site_name, success: true });
      } catch (error) {
        results.push({ site: articleData.site_name, success: false });
      }
    }

    const successCount = results.filter(r => r.success).length;
    if (successCount === results.length) {
      const successMsg = publishStatus === 'publish' 
        ? '🎉 Tous les articles publiés avec succès !'
        : '📝 Tous les articles enregistrés en brouillon !';
      toast.success(successMsg);
    } else {
      toast.warning(`${successCount}/${results.length} articles traités`);
    }

    setTimeout(() => navigate('/publications'), 2000);
  };

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="auto-edito-express">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 md:mb-12 text-center">
          <div className="flex items-center justify-center mb-4">
            <Rocket className="w-12 h-12 md:w-16 md:h-16 text-sky-400 text-glow-strong" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold font-orbitron tracking-widest uppercase text-sky-400 text-glow mb-4">
            Auto Edito Express
          </h1>
          <p className="text-base md:text-lg text-slate-400 font-rajdhani">
            Générez et publiez sur plusieurs sites en un seul clic
          </p>
        </div>

        {!generatedArticles ? (
          <div className="space-y-6">
            {/* Voice Recording */}
            <Card className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60 hud-marker">
              <CardHeader>
                <CardTitle className="text-xl md:text-2xl font-orbitron uppercase text-sky-400">
                  🎙️ Enregistrez votre contenu
                </CardTitle>
              </CardHeader>
              <CardContent>
                <VoiceRecorder onRecordingComplete={handleRecordingComplete} uploading={uploading} />
                
                {uploadedAudio && (
                  <div className="mt-6 p-4 bg-green-500/10 border-2 border-green-500/30 rounded">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-green-400 text-sm font-semibold">✓ Audio transcrit</p>
                      <span className="text-xs text-slate-500">Modifiez si nécessaire</span>
                    </div>
                    <Textarea
                      value={uploadedAudio.transcription || ''}
                      onChange={(e) => setUploadedAudio({...uploadedAudio, transcription: e.target.value})}
                      className="min-h-[100px] bg-slate-950/50 border-slate-700 text-slate-100 text-sm"
                      placeholder="Transcription de votre audio..."
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Additional Notes */}
            <Card className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60 hud-marker">
              <CardHeader>
                <CardTitle className="text-xl md:text-2xl font-orbitron uppercase text-sky-400">
                  ✍️ Compléments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Text Notes */}
                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center mb-2">
                    <Edit className="w-4 h-4 mr-2" />
                    Notes texte
                  </label>
                  <Textarea
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    placeholder="Ajoutez des précisions, des angles spécifiques, des consignes..."
                    className="min-h-[100px] bg-slate-950/50 border-slate-800 text-slate-100"
                  />
                </div>

                {/* URLs */}
                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center mb-2">
                    <Link className="w-4 h-4 mr-2" />
                    URLs de référence
                  </label>
                  <Textarea
                    value={additionalUrls}
                    onChange={(e) => setAdditionalUrls(e.target.value)}
                    placeholder="Collez des URLs (une par ligne) pour enrichir le contexte..."
                    className="min-h-[80px] bg-slate-950/50 border-slate-800 text-slate-100 font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">Articles, sources, références à analyser</p>
                </div>

                {/* File Attachments */}
                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center mb-2">
                    <Paperclip className="w-4 h-4 mr-2" />
                    Pièces jointes
                  </label>
                  
                  <div className="border-2 border-dashed border-slate-700 rounded-lg p-4 text-center hover:border-sky-500/50 transition-colors">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      {uploadingFiles ? (
                        <div className="flex items-center justify-center text-sky-400">
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Upload en cours...
                        </div>
                      ) : (
                        <div className="flex flex-col items-center text-slate-400 hover:text-sky-400 transition-colors">
                          <Upload className="w-8 h-8 mb-2" />
                          <span className="text-sm">Cliquez ou glissez des fichiers</span>
                          <span className="text-xs text-slate-500 mt-1">PDF, Word, TXT, Excel</span>
                        </div>
                      )}
                    </label>
                  </div>

                  {/* Uploaded Files List */}
                  {additionalFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {additionalFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between bg-slate-900/50 border-2 border-slate-800 rounded p-2">
                          <div className="flex items-center">
                            <Paperclip className="w-4 h-4 text-sky-400 mr-2" />
                            <span className="text-sm text-slate-300 truncate max-w-[200px]">{file.name}</span>
                          </div>
                          <button
                            onClick={() => removeFile(file.id)}
                            className="text-slate-500 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Site Selection */}
            <Card className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60 hud-marker">
              <CardHeader>
                <CardTitle className="text-xl md:text-2xl font-orbitron uppercase text-sky-400">
                  🎯 Sélectionnez les sites
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sites.length === 0 ? (
                  <p className="text-slate-400 text-center py-4">
                    Aucun site configuré. <button onClick={() => navigate('/wordpress')} className="text-sky-400 underline">Ajoutez un site</button>
                  </p>
                ) : (
                  <div className="space-y-4">
                    {sites.map((site) => (
                      <div
                        key={site.id}
                        onClick={() => toggleSite(site.id)}
                        className={`p-4 border rounded cursor-pointer transition-all ${
                          selectedSites.includes(site.id)
                            ? 'bg-sky-500/10 border-sky-500/50 glow-cyan'
                            : 'bg-slate-900/50 border-slate-800 hover:border-sky-500/30'
                        }`}
                      >
                        <div className="flex items-start">
                          <Checkbox
                            checked={selectedSites.includes(site.id)}
                            className="mt-1 mr-3"
                          />
                          <div className="flex-1">
                            <p className="text-sky-300 font-bold text-sm md:text-base">{site.name}</p>
                            {site.pitch && (
                              <p className="text-xs md:text-sm text-slate-400 mt-1">
                                📐 Angle: {site.pitch}
                              </p>
                            )}
                            <p className="text-xs text-slate-500 mt-1">{site.url}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Generate Button */}
            <div className="flex justify-center">
              <Button
                onClick={handleGenerate}
                disabled={generating || (!uploadedAudio && !additionalNotes) || selectedSites.length === 0}
                className="bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border-2 border-sky-500/50 hover:border-sky-400 uppercase tracking-widest font-bold px-12 py-8 text-base md:text-lg transition-all duration-300 glow-sky hover:glow-sky-strong"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                    Génération en cours...
                  </>
                ) : (
                  <>
                    <Rocket className="w-6 h-6 mr-3" />
                    Générer {selectedSites.length} article{selectedSites.length > 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          // Results & Preview
          <div className="space-y-6">
            <Card className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60 hud-marker">
              <CardHeader>
                <CardTitle className="text-2xl font-orbitron uppercase text-green-400 flex items-center justify-between">
                  <span>✓ {Object.keys(generatedArticles).length} article(s) prêt(s)</span>
                  <Button
                    onClick={() => setGeneratedArticles(null)}
                    className="text-sm bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 border-2 border-slate-600 px-4 py-2"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Annuler
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {Object.entries(generatedArticles).map(([siteId, articleData]) => (
                    <Card key={siteId} className="bg-slate-900/50 border-slate-800">
                      <CardContent className="p-4 md:p-6">
                        {/* Site Name */}
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sky-400 font-bold text-lg">{articleData.site_name}</h3>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleEditArticle(siteId, articleData)}
                              className="text-xs bg-slate-800 hover:bg-slate-700 text-sky-400 border-2 border-sky-500/30 px-3 py-2"
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Éditer
                            </Button>
                            <Button
                              onClick={() => navigate(`/article/${articleData.article_id}`)}
                              className="text-xs bg-slate-800 hover:bg-slate-700 text-purple-400 border-2 border-purple-500/30 px-3 py-2"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              Voir
                            </Button>
                          </div>
                        </div>

                        {/* Featured Image */}
                        {articleData.featured_image_url && (
                          <div className="mb-4 relative group">
                            <img
                              src={articleData.featured_image_url.startsWith('http') ? articleData.featured_image_url : `${STATIC_URL}${articleData.featured_image_url}`}
                              alt="Featured"
                              className="w-full h-48 object-cover rounded border-2 border-slate-700"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <Button
                              onClick={() => handleRegenerateImage(siteId, articleData.article_id)}
                              disabled={regeneratingImage === articleData.article_id}
                              className="absolute top-2 right-2 text-xs bg-slate-950/80 hover:bg-slate-900 text-sky-400 border-2 border-sky-500/50 px-3 py-2"
                            >
                              {regeneratingImage === articleData.article_id ? (
                                <>
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  Génération...
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Régénérer
                                </>
                              )}
                            </Button>
                          </div>
                        )}

                        {/* Article Info */}
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Titre</p>
                            <p className="text-slate-200 font-semibold">{articleData.title}</p>
                          </div>

                          <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Résumé</p>
                            <p className="text-sm text-slate-400">{articleData.summary}</p>
                          </div>

                          {/* SEO Info */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-slate-800">
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">SEO Titre</p>
                              <p className="text-xs text-slate-400">{articleData.seo_title}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">SEO Description</p>
                              <p className="text-xs text-slate-400">{articleData.seo_description}</p>
                            </div>
                          </div>

                          {/* Key Figure - pour Economiematin */}
                          {(articleData.key_figure || articleData.key_figure_legend) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-slate-800 bg-purple-500/5 p-3 rounded">
                              <div>
                                <p className="text-xs text-purple-400 uppercase tracking-wider mb-1">📊 Chiffre clé</p>
                                <p className="text-lg font-bold text-purple-300">{articleData.key_figure || '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-purple-400 uppercase tracking-wider mb-1">Légende</p>
                                <p className="text-sm text-slate-400">{articleData.key_figure_legend || '-'}</p>
                              </div>
                            </div>
                          )}

                          {/* Keywords */}
                          {articleData.keywords && articleData.keywords.length > 0 && (
                            <div className="pt-3 border-t border-slate-800">
                              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Mots-clés</p>
                              <div className="flex flex-wrap gap-2">
                                {articleData.keywords.map((keyword, idx) => (
                                  <span key={idx} className="text-xs bg-sky-500/10 text-sky-400 px-2 py-1 rounded border-2 border-sky-500/30">
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col md:flex-row gap-4 mt-8">
                  <Button
                    onClick={() => handlePublishAll('draft')}
                    className="flex-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border-2 border-amber-500/50 hover:border-amber-400 uppercase tracking-widest font-bold py-6 text-base md:text-lg rounded-lg"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    Enregistrer en brouillon
                  </Button>
                  <Button
                    onClick={() => handlePublishAll('publish')}
                    className="flex-1 bg-green-500/10 hover:bg-green-500/20 text-green-400 border-2 border-green-500/50 hover:border-green-400 uppercase tracking-widest font-bold py-6 text-base md:text-lg rounded-lg"
                  >
                    <Check className="w-5 h-5 mr-2" />
                    Publier directement
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Edit Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
              <DialogContent className="bg-slate-950 border-sky-500/50 max-h-[90vh] overflow-y-auto max-w-4xl">
                <DialogHeader>
                  <DialogTitle className="text-xl md:text-2xl font-orbitron text-sky-400">Éditer l'article</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm text-slate-400 uppercase tracking-wider">Titre</label>
                    <Input
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-slate-400 uppercase tracking-wider">Contenu</label>
                    <Textarea
                      value={editForm.content}
                      onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                      className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 min-h-[300px]"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-slate-400 uppercase tracking-wider">Résumé</label>
                    <Textarea
                      value={editForm.summary}
                      onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })}
                      className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 min-h-[80px]"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-slate-400 uppercase tracking-wider">SEO Titre</label>
                      <Input
                        value={editForm.seo_title}
                        onChange={(e) => setEditForm({ ...editForm, seo_title: e.target.value })}
                        className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-slate-400 uppercase tracking-wider">SEO Description</label>
                      <Input
                        value={editForm.seo_description}
                        onChange={(e) => setEditForm({ ...editForm, seo_description: e.target.value })}
                        className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={handleSaveEdit}
                    className="w-full bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border-2 border-sky-500/50 hover:border-sky-400 uppercase tracking-widest font-bold py-6"
                  >
                    Sauvegarder
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoEditoExpress;
